import "./AppCompanyHolidayNew.css";
import { CompanyHoliday, CompanyHolidayType, DC_COMPONENT_LABELS } from "winslow-core";
import DatePicker from "react-datepicker";  
import { selectUIStates, selectUser, setUIStates } from "../../../redux/features/appData";
import { useDispatch, useSelector } from "react-redux";
import { useEffect, useState } from "react";
import { apiGetCompanyHolidaysForWorkspace } from "../../../services/PaidTimeOffPolicyService";
import { MySwitch } from "../../shared/MySwitch";
import { Radio } from "../../../ds2/components/Radio";
import { AppChanges } from "../app-changes/AppChanges";
import { useNavigate } from "react-router-dom";
import { POLICY_EDITOR_TABS } from "./AppTimeOff";

type AppCompanyHolidayNewProps = {
    dynamicContext?: {
        companyHolidayId?: string; // Optional context for company holiday
    };
    disabled?: boolean;    
    onNavigation?: (
        newObjectName: DC_COMPONENT_LABELS, 
        newLabel: string,
        data: any
      ) => void;
    companyHoliday?: CompanyHoliday;
    onCancel?: () => void;
}

export const AppCompanyHolidayNew = ({dynamicContext, disabled = false, onNavigation, companyHoliday, onCancel}: AppCompanyHolidayNewProps) => {

    const navigate = useNavigate();
    const dispatch = useDispatch();

    const storeUser = useSelector(selectUser);
    const uiStates = useSelector(selectUIStates);

    const [newCompanyHoliday, setNewCompanyHoliday] = useState<CompanyHoliday>();
    const [hasChanges, setHasChanges] = useState<boolean>(false);

    useEffect(() => {
        if (companyHoliday) {
            setNewCompanyHoliday({...companyHoliday});
        } else if (dynamicContext?.companyHolidayId) {
            (async () => {
                const companyHolidays = await apiGetCompanyHolidaysForWorkspace(dynamicContext?.companyHolidayId || "");

                if (companyHolidays) {
                    const theHoliday = companyHolidays.find((holiday) => holiday.id === dynamicContext.companyHolidayId);   
                    if (theHoliday) {
                        setNewCompanyHoliday(theHoliday);
                    }
                }
            })();
        } else {
            const { dayOfWeek, month, year, occurrence } = getNthInformation(new Date());

            setNewCompanyHoliday({
                id: "",
                workspaceId: storeUser?.currentWorkspaceId || "",
                name: "",
                type: CompanyHolidayType.DATE,
                date: new Date().toISOString(),
                dayOfWeek: dayOfWeek,
                month: month,
                nth: occurrence,
                repeats: true,
            });
        }
    }, [companyHoliday, dynamicContext, storeUser]);

    useEffect(() => {
        dispatch(setUIStates({
            ...uiStates,
            hrisLocation: companyHoliday ? "Edit Company Holiday" : "Add Company Holiday"
        }));
    }, [companyHoliday]);


    const choiceLabels = [
        {
            type: CompanyHolidayType.NTH,
            label: "Nth Day of the Month",
        },
        {
            type: CompanyHolidayType.DATE,
            label: "Same Date",
        }
    ]
     

    const getRepeatTypeChoices = () => {
        return choiceLabels.map((choice) => {
            return choice.label;
        });
    }

    const getRepeatLableFromChoice = (choice: CompanyHolidayType) => {
        return choiceLabels.find((tryChoice) => tryChoice.type === choice)?.label;
    }

    const getRepeatTypeFromLabel = (label: string) => {
        return choiceLabels.find((choice) => choice.label === label)?.type;
    }

    const getNthInformation = (fromDate: Date) => {
        const month = fromDate.getMonth() + 1;
        const year = fromDate.getFullYear();
        const dayOfWeek = fromDate.getDay(); // 0 = Sunday, 1 = Monday, etc.
        const dayOfMonth = fromDate.getDate();
        
        // Calculate which occurrence of this day of the week it is
        const firstDayOfMonth = new Date(year, fromDate.getMonth(), 1);
        const firstOccurrenceOfDay = new Date(year, fromDate.getMonth(), 1);
        
        // Find the first occurrence of this day of the week in the month
        while (firstOccurrenceOfDay.getDay() !== dayOfWeek) {
            firstOccurrenceOfDay.setDate(firstOccurrenceOfDay.getDate() + 1);
        }
        
        // Calculate which occurrence (1st, 2nd, 3rd, 4th, or last)
        const occurrence = Math.floor((dayOfMonth - firstOccurrenceOfDay.getDate()) / 7) + 1;
        
        // Check if it's the last occurrence of this day in the month
        const lastDayOfMonth = new Date(year, fromDate.getMonth() + 1, 0);
        const lastOccurrenceOfDay = new Date(year, fromDate.getMonth() + 1, 0);
        
        // Find the last occurrence of this day of the week in the month
        while (lastOccurrenceOfDay.getDay() !== dayOfWeek) {
            lastOccurrenceOfDay.setDate(lastOccurrenceOfDay.getDate() - 1);
        }
        
        const isLastOccurrence = dayOfMonth === lastOccurrenceOfDay.getDate();
        
        return {
            month: month,
            dayOfWeek: dayOfWeek,
            occurrence: isLastOccurrence ? -1 : occurrence, // -1 indicates "last"
            year: year
        }
    }

    const getDayOfWeekLabel = (dayOfWeek: number) => {
        const dayOfWeekLabels = [
            "Sunday",
            "Monday",
            "Tuesday",
            "Wednesday",
            "Thursday",
            "Friday",
            "Saturday",
        ]

        return dayOfWeekLabels[dayOfWeek] || `Day ${dayOfWeek + 1}`;
    }    

    const getNthLabel = (nth: number) => {
        if (nth === -1) {
            return "last";
        }

        const nthLabels = [
            "1st",
            "2nd",
            "3rd",
            "4th",
            "5th",
        ]

        return nthLabels[nth - 1] || "";
    }

    const getMonthLabel = (month: number) => {
        const monthLabels = [
            "January",
            "February",
            "March",
            "April",
            "May",
            "June",
            "July",
            "August",
            "September",
            "October",
            "November",
            "December",
        ]

        return monthLabels[month - 1] || "";
    }

    const handleSave = () => {
        console.log("[AppCompanyHolidayNew] newCompanyHoliday: ", newCompanyHoliday);
    }

    const handleCancel = () => {
        console.log("[AppCompanyHolidayNew] newCompanyHoliday: ", newCompanyHoliday);
        console.log("[AppCompanyHolidayNew] onNavigation: ", onNavigation);
        console.log("[AppCompanyHolidayNew] onCancel: ", onCancel);
        if (onNavigation) {
            onNavigation("pto-policy", "list", null);
        } else {
            onCancel?.();
        }
    }
    
    return (    
        <div className="app-company-holidays-new">
            <div className="app-company-holidays-new-contents">
                <div className="app-company-holidays-new-left">
                    <div className="app-custom-property-label">
                        Holiday Name
                    </div>
                    <div className="app-custom-property-value">
                        <input 
                            type="text" 
                            placeholder="Enter holiday name"
                            value={newCompanyHoliday?.name}
                            onChange={(e) => {
                                if (newCompanyHoliday) {
                                    setNewCompanyHoliday({
                                        ...newCompanyHoliday,
                                        name: e.target.value,
                                    })
                                    setHasChanges(true);
                                }
                            }}
                        />
                    </div>

                    <div className="app-custom-property-label">
                        Date
                    </div>
                    <div className="app-custom-property-value">
                        <DatePicker
                            selected={newCompanyHoliday?.date ? new Date(newCompanyHoliday.date) : null} 
                            dateFormat={"MM/dd/yyyy"}
                            onChange={(date) => {
                                if (newCompanyHoliday) {    
                                    const { dayOfWeek, month, year, occurrence } = getNthInformation(date || new Date());
                                    setNewCompanyHoliday({
                                        ...newCompanyHoliday,
                                        date: date?.toISOString() || "",
                                        dayOfWeek: dayOfWeek,
                                        month: month,
                                        nth: occurrence,
                                    });
                                    setHasChanges(true);
                                } 
                            }}
                            className="date-picker-input"
                            wrapperClassName="app-date-picker-wrapper"
                        />
                        
                    </div>
                    {newCompanyHoliday?.type === CompanyHolidayType.NTH &&
                        <div className="app-company-holidays-new-nth-information">
                            {`Repeats ${getNthLabel(newCompanyHoliday?.nth || 0)} ${getDayOfWeekLabel(newCompanyHoliday?.dayOfWeek || 0)} of ${getMonthLabel(newCompanyHoliday?.month || 0)}`}
                        </div>
                    }

                    {newCompanyHoliday?.date &&
                        <div className="app-company-holidays-new-repeat">
                            <div className="app-custom-property-group">
                                <MySwitch                                 
                                    id="repeats"
                                    checked={newCompanyHoliday?.repeats}
                                    onChange={(checked: boolean) => {
                                        if (newCompanyHoliday) {    
                                            setNewCompanyHoliday({
                                                ...newCompanyHoliday,
                                                repeats: checked,
                                            })
                                            setHasChanges(true);
                                        }
                                    }}
                                />
                                <label htmlFor="repeats">Repeats annually</label>
                            </div>
                        </div>
                    }

                    {newCompanyHoliday?.date && newCompanyHoliday?.repeats &&
                        <div className="app-company-holidays-new-repeat-options">
                            <div className="app-custom-property-label">
                                Repeat Type
                            </div>
                            <div className="app-custom-property-radio">
                                <div className="app-custom-property-group">
                                    <Radio
                                        name="holiday-type"
                                        choices={getRepeatTypeChoices()}
                                        choice={getRepeatLableFromChoice(newCompanyHoliday?.type || CompanyHolidayType.DATE) || ""}
                                        onChange={(choice: string) => {
                                            if (newCompanyHoliday) {
                                                setNewCompanyHoliday({
                                                        ...newCompanyHoliday,
                                                        type: getRepeatTypeFromLabel(choice) || CompanyHolidayType.DATE,
                                                    })
                                                setHasChanges(true);
                                            }
                                        }}
                                    />
                                </div>
                                {/* <div className="app-custom-property-group">
                                    <Radio
                                        name="holiday-type"
                                        choices={[CompanyHolidayType.DATE, CompanyHolidayType.NTH]}
                                        choice={newCompanyHoliday?.type || CompanyHolidayType.NTH}
                                        onChange={(choice: string) => {
                                            setNewCompanyHoliday({
                                                ...newCompanyHoliday,
                                                type: choice as CompanyHolidayType,
                                            })
                                            setHasChanges(true);
                                        }}
                                    />
                                </div> */}
                            </div>
                        </div>
                    }
                </div>
                {hasChanges &&
                    <AppChanges
                        saveDisabled={false}
                        onSubmit={handleSave}
                        onCancel={handleCancel}
                    />
                }
            </div>
        </div>
    )
}